import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Space, Spin, Typography } from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { ActionFlowRequestDTO, AppapproDTO } from '@/shared/api/model';
import { useFindByDomainAndDomainId } from '@/shared/api/client/file-upload-controller';

const { Text } = Typography;

interface DeductionFormViewProps {
    data?: ActionFlowRequestDTO;
    formikProps?: any;
}

/**
 * Used in the MEMBER_DEDUCTION_FORM node — the member downloads the official deduction form
 * that was uploaded by the loan officer, gets it signed by their employer, and then uploads
 * the signed copy in the next step (DocumentUploadView).
 */
const DeductionFormView: React.FC<DeductionFormViewProps> = ({ data, formikProps }) => {
    const values = formikProps?.values || data || {};
    const valueData: AppapproDTO = values.valueData || {};
    // Prioritize hydrated valueData over potentially empty actionFlow valueData
    const branch = valueData.branch || data?.valueData?.branch || '';
    const loanId = String(valueData.loanId || valueData.id || data?.valueData?.loanId || data?.valueData?.id || '');
    const domainId = branch && loanId ? `${branch}.${loanId}` : '';

    const { data: docsResponse, isLoading } = useFindByDomainAndDomainId(
        'LOAN_APPLICATION',
        domainId,
        // @ts-ignore
        { enabled: !!domainId }
    );

    const [officialForm, setOfficialForm] = useState<any>(null);

    useEffect(() => {
        if (docsResponse?.data && Array.isArray(docsResponse.data)) {
            const found = docsResponse.data.find(
                (d: any) => (d.documentType || '').toUpperCase() === 'OFFICIAL_DEDUCTION_FORM' ||
                             (d.category || '').toLowerCase() === 'official_deduction_form' ||
                             (d.role || '').toLowerCase() === 'official_deduction_form'
            );
            setOfficialForm(found || null);
        }
    }, [docsResponse]);

    const openDocument = (id: string, download = false) => {
        const url = `/api/files/preview/documents/${id}${download ? '?download=true' : ''}`;
        window.open(url, '_blank');
    };

    return (
        <Card title="Loan Deduction Form" style={{ marginBottom: 16 }}>
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: 32 }}>
                    <Spin tip="Checking for deduction form..." />
                </div>
            ) : officialForm ? (
                <>
                    <Alert
                        message="Official Deduction Form Ready"
                        description={
                            <ol style={{ paddingLeft: 20, margin: 0 }}>
                                <li>Download the official deduction form below.</li>
                                <li>Print it and have it signed by your employer.</li>
                                <li>Upload the signed copy in the next step.</li>
                            </ol>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <Space>
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => openDocument(officialForm.id, false)}
                        >
                            View Form
                        </Button>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={() => openDocument(officialForm.id, true)}
                        >
                            Download Form
                        </Button>
                    </Space>
                </>
            ) : (
                <Alert
                    message="Deduction Form Not Yet Available"
                    description="The loan officer has not yet uploaded the official deduction form. This page refreshes automatically — please check back shortly or contact the loan officer."
                    type="warning"
                    showIcon
                />
            )}
        </Card>
    );
};

export default DeductionFormView;
